/**
 * QbMaintenanceModal
 *
 * Console-style modal for running allowlisted QuickBox CLI operations.
 * Shows live streaming output via SSE with auto-scroll, copy, and line-wrap toggle.
 *
 * Resilience strategy:
 *   1. Primary: SSE stream via EventSource for live output + terminal status event
 *   2. Fallback: If SSE disconnects, polls GET /api/admin/qb/status until terminal
 *   3. Terminal states: "completed" or "failed" — always fires onComplete for refresh
 */

import { useCallback, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { Button } from "../ui/Button";
import { Icon } from "../ui/Icon";
import { cn } from "../../utils/cn";
import { useConsoleScroll } from "../../hooks/useConsoleScroll";
import { useJobStream } from "../../hooks/useJobStream";
import { ConsolePanel } from "../system/ConsolePanel";

type QbAction = "fix_version" | "update_quickbox" | "update_emby" | "update_jellyfin";
type JobStatus = "idle" | "confirming" | "running" | "completed" | "failed";

type QbMaintenanceModalProps = {
  open: boolean;
  onClose: () => void;
  onComplete?: () => void;
  initialAction?: QbAction;
  /** When true, skip the action picker and run the initial action immediately */
  skipConfirmation?: boolean;
  /** Use beta/unstable channel for Emby/Jellyfin updates */
  beta?: boolean;
};

const ACTION_CONFIG: Record<QbAction, { label: string; description: string; icon: string }> = {
  fix_version: {
    label: "Fix version",
    description: "Runs 'qb fix version' to repair version tracking and dependencies.",
    icon: "lucide:wrench",
  },
  update_quickbox: {
    label: "Update QuickBox",
    description: "Runs 'qb update quickbox' to update QuickBox to the latest version.",
    icon: "lucide:refresh-cw",
  },
  update_emby: {
    label: "Update Emby Server",
    description: "Runs 'qb update emby' to update the Emby media server to the latest version.",
    icon: "simple-icons:emby",
  },
  update_jellyfin: {
    label: "Update Jellyfin Server",
    description: "Runs 'qb update jellyfin' to update the Jellyfin media server to the latest version.",
    icon: "simple-icons:jellyfin",
  },
};

const STATUS_BADGES: Record<JobStatus, { label: string; className: string }> = {
  idle: { label: "Idle", className: "bg-muted text-muted-foreground" },
  confirming: { label: "Confirming", className: "bg-yellow-500/20 text-yellow-400" },
  running: { label: "Running", className: "bg-blue-500/20 text-blue-400 animate-pulse" },
  completed: { label: "Completed", className: "bg-emerald-500/20 text-emerald-400" },
  failed: { label: "Failed", className: "bg-red-500/20 text-red-400" },
};

export const QbMaintenanceModal = ({ open, onClose, onComplete, initialAction, skipConfirmation, beta: betaProp }: QbMaintenanceModalProps) => {
  const [status, setStatus] = useState<JobStatus>("confirming");
  const [action, setAction] = useState<QbAction>(initialAction ?? "fix_version");
  const [betaToggle, setBetaToggle] = useState(betaProp ?? false);

  const onCompleteRef = useRef(onComplete);
  const autoStartedRef = useRef(false);
  onCompleteRef.current = onComplete;
  const titleId = useId();

  const stream = useJobStream({
    streamUrl: "/api/admin/qb/stream",
    statusUrl: "/api/admin/qb/status",
  });

  const scroll = useConsoleScroll(stream.lines.length);

  // Sync terminal status from stream
  useEffect(() => {
    if (stream.isTerminal && status === "running") {
      setStatus(stream.status as "completed" | "failed");
      onCompleteRef.current?.();
    }
  }, [stream.isTerminal, stream.status, status]);

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setStatus(skipConfirmation ? "running" : "confirming");
      setAction(initialAction ?? "fix_version");
      setBetaToggle(betaProp ?? false);
      stream.reset();
    }
  }, [open, initialAction, skipConfirmation, betaProp]); // eslint-disable-line react-hooks/exhaustive-deps

  const startJob = useCallback(async () => {
    setStatus("running");
    stream.reset();

    try {
      const body: Record<string, unknown> = { action };
      if ((action === "update_emby" || action === "update_jellyfin") && betaToggle) {
        body.beta = true;
      }

      const res = await fetch("/api/admin/qb/run", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });

      const data = await res.json() as Record<string, unknown>;

      if (!res.ok || data.ok !== true) {
        const errMsg = (data.error as Record<string, unknown> | undefined)?.message ?? data.message ?? "Failed to start QuickBox operation";
        stream.reset();
        setStatus("failed");
        return;
      }

      stream.connect();
    } catch (err) {
      stream.reset();
      setStatus("failed");
    }
  }, [action, betaToggle, stream]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-start when skipConfirmation is set
  useEffect(() => {
    if (open && skipConfirmation && status === "running" && !autoStartedRef.current) {
      autoStartedRef.current = true;
      void startJob();
    }
    if (!open) {
      autoStartedRef.current = false;
    }
  }, [open, skipConfirmation, status, startJob]);

  const handleClose = useCallback(() => {
    stream.disconnect();
    onClose();
  }, [onClose, stream]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open) return null;

  const badge = STATUS_BADGES[status];
  const isTerminal = status === "completed" || status === "failed";
  const actionConfig = ACTION_CONFIG[action];

  const modal = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-in fade-in-0 duration-200"
        onClick={isTerminal || status === "confirming" ? handleClose : undefined}
      />
      <div role="dialog" aria-modal="true" aria-labelledby={titleId} className="relative w-full max-w-3xl mx-4 animate-in fade-in-0 zoom-in-95 duration-200">
        <div className="rounded-xl border border-white/[0.08] bg-card shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-white/[0.06]">
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/[0.04]">
                <Icon icon="lucide:terminal" className="h-4 w-4 text-foreground" />
              </div>
              <div>
                <h2 id={titleId} className="text-sm font-semibold text-foreground">System Response</h2>
                <p className="text-[10px] text-muted-foreground/70">QuickBox Maintenance</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className={cn("inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-medium", badge.className)}>
                {badge.label}
              </span>
              <button
                type="button"
                onClick={handleClose}
                disabled={status === "running"}
                aria-label="Close dialog"
                className="flex h-7 w-7 items-center justify-center rounded-md hover:bg-white/[0.06] transition-colors disabled:opacity-40"
              >
                <Icon icon="lucide:x" className="h-4 w-4 text-muted-foreground" />
              </button>
            </div>
          </div>

          {/* Confirmation view */}
          {status === "confirming" && (
            <div className="px-5 py-6 space-y-4">
              <p className="text-sm text-foreground">Select a maintenance operation:</p>
              <div className="space-y-2">
                {(["fix_version", "update_quickbox", "update_emby", "update_jellyfin"] as const).map((a) => {
                  const config = ACTION_CONFIG[a];
                  return (
                    <label
                      key={a}
                      className={cn(
                        "flex items-center gap-3 rounded-lg border p-3 cursor-pointer transition-colors",
                        action === a
                          ? "border-blue-500/50 bg-blue-500/5"
                          : "border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.04]"
                      )}
                    >
                      <input
                        type="radio"
                        name="qb-action"
                        value={a}
                        checked={action === a}
                        onChange={() => setAction(a)}
                        className="accent-blue-500"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Icon icon={config.icon} className="h-4 w-4 text-muted-foreground" />
                          <p className="text-sm font-medium text-foreground">{config.label}</p>
                        </div>
                        <p className="text-[10px] text-muted-foreground/70 mt-0.5">{config.description}</p>
                      </div>
                    </label>
                  );
                })}
              </div>
              {(action === "update_emby" || action === "update_jellyfin") && (
                <label className="flex items-center gap-2 rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={betaToggle}
                    onChange={(e) => setBetaToggle(e.target.checked)}
                    className="accent-amber-500"
                  />
                  <div className="flex-1">
                    <span className="text-xs font-medium text-foreground">Use beta/unstable channel</span>
                    <p className="text-[10px] text-muted-foreground/70">
                      Install the latest pre-release version instead of stable.
                    </p>
                  </div>
                </label>
              )}
              {(action === "update_quickbox" || action === "update_emby" || action === "update_jellyfin") && (
                <div className="flex items-start gap-2 rounded-lg border border-yellow-500/30 bg-yellow-500/5 px-3 py-2">
                  <Icon icon="lucide:alert-triangle" className="h-4 w-4 text-yellow-400 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-yellow-200/90">
                    {action === "update_quickbox"
                      ? "Updating QuickBox may take several minutes and could require service restarts."
                      : `Updating ${action === "update_emby" ? "Emby" : "Jellyfin"} may take several minutes. Active sessions will be interrupted and the server will restart.`}
                  </p>
                </div>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="ghost" size="sm" onClick={handleClose}>
                  Cancel
                </Button>
                <Button variant="primary" size="sm" onClick={() => void startJob()}>
                  <Icon icon={actionConfig.icon} className="h-3.5 w-3.5 mr-1.5" />
                  {actionConfig.label}
                </Button>
              </div>
            </div>
          )}

          {/* Console output view */}
          {status !== "confirming" && (
            <>
              <ConsolePanel
                lines={stream.lines}
                consoleRef={scroll.consoleRef}
                onScroll={scroll.handleScroll}
                wrapLines={scroll.wrapLines}
                onToggleWrap={() => scroll.setWrapLines((prev) => !prev)}
                autoScroll={scroll.autoScroll}
                onScrollToBottom={scroll.scrollToBottom}
                onCopy={() => scroll.handleCopyOutput(stream.lines)}
                durationMs={stream.durationMs}
                exitCode={stream.exitCode}
                isLoading={status === "running"}
                error={stream.error}
              />

              {isTerminal && (
                <div className="flex items-center justify-between gap-3 px-5 py-3 border-t border-white/[0.06]">
                  <div className="text-xs text-muted-foreground/70">
                    {status === "completed"
                      ? "Operation completed successfully."
                      : `Operation failed${stream.exitCode !== null ? ` (exit code ${stream.exitCode})` : ""}.`}
                  </div>
                  <Button variant="secondary" size="sm" onClick={handleClose}>
                    Close
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};
